import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { findNavItemByPath } from "../../config/navigation";
import { useAuthInfo } from "../../context/AuthInfoContext";
import { getRuntimeConfig } from "../../config/authConfig";
import { clearAuthSessionState } from "../../utils/authRedirect";
import Icon from "../common/Icon";
import ActivationControl from "./ActivationControl";
import { grantIsActive, levelName } from "../../utils/capabilities";

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "U";
  const base = nameOrEmail.includes("@") ? nameOrEmail.split("@")[0] : nameOrEmail;
  const parts = base.split(/[.\s_-]+/).filter(Boolean);
  const initials = (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  return (initials || base[0] || "U").toUpperCase();
}

// Groepsniveau in het kruimelpad enkel tonen als het iets toevoegt: "Bankflow / Bankflow"
// wordt gewoon "Bankflow".
export function crumbShowsGroup(nav) {
  if (!nav?.group) return false;
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  return norm(nav.group) !== norm(nav.item?.label);
}

// Rollabels (issue #23): de rollen uit auth/me `access.roles` (naam, bereik, niveau). F5: enkel nog toewijzingen uit de
// app (geen Entra-app-rollen meer); zonder access-blok de rolcodes uit auth/me `roles` zoals ze zijn.
// { short: voor de topbalk (eerste rol + aantal), full: voor het accountmenu }
export function roleLabelsOf({ roles, access } = {}) {
  const granted = Array.isArray(access?.roles) ? access.roles.filter(Boolean) : [];
  if (granted.length > 0) {
    const names = granted.map((r) => r.name || r.code);
    const short = names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
    const full = granted
      .map((r) => {
        const parts = [r.name || r.code];
        if (r.maxLevel && String(r.maxLevel).toUpperCase() !== "G") parts.push(`niveau ${levelName(r.maxLevel)}`);
        if (r.scope && r.scope.all === false && r.scope.rules?.length) parts.push("deelbereik");
        if (r.isActivatable && !grantIsActive(r)) parts.push("niet geactiveerd");
        return parts.join(" · ");
      })
      .join(", ");
    return { short, full };
  }
  const legacy = (Array.isArray(roles) ? roles : []).filter(Boolean).join(", ");
  return { short: legacy, full: legacy };
}

export default function Topbar({ onToggleSidebar, navOpen = false, menuButtonRef, sidebarId }) {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const { roles, access } = useAuthInfo();

  const active = useMemo(() => instance.getActiveAccount?.() || accounts?.[0], [instance, accounts]);
  const name = active?.name || "";
  const email = active?.username || "";
  const initials = getInitials(name || email);
  const { short: roleLabel, full: roleLabelFull } = roleLabelsOf({ roles, access });

  const envLabel = (getRuntimeConfig()?.db?.environment || "").toUpperCase();

  const nav = findNavItemByPath(location.pathname);

  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className="app-topbar">
      <div className="app-topbar__start">
        {/* De knop bestaat alleen in drawer-modus: AppShell geeft onToggleSidebar door als
            null zodra de zijbalk vast staat. Bewust géén display:none op een altijd
            gerenderde knop — die inline stijl wint van elke klasse en dwong de CSS tot
            een !important. */}
        {onToggleSidebar ? (
          <button
            ref={menuButtonRef}
            type="button"
            className="app-topbar-menu-btn"
            aria-label="Menu"
            aria-expanded={navOpen}
            aria-controls={sidebarId}
            onClick={onToggleSidebar}
          >
            <Icon name="menu" size={20} />
          </button>
        ) : null}
        <nav className="app-breadcrumb" aria-label="Kruimelpad">
          <ol className="app-breadcrumb__list">
            <li>Manuco Invest</li>
            {nav ? (
              <>
                {crumbShowsGroup(nav) ? (
                  <li>
                    <span className="app-breadcrumb__sep" aria-hidden="true">
                      /
                    </span>
                    {nav.group}
                  </li>
                ) : null}
                <li className="app-breadcrumb__current" aria-current="page">
                  <span className="app-breadcrumb__sep" aria-hidden="true">
                    /
                  </span>
                  {nav.item.label}
                </li>
              </>
            ) : null}
          </ol>
        </nav>
      </div>
      {isAuthenticated ? (
        <div className="app-topbar__end">
          {envLabel ? <span className="mono app-topbar__env">{envLabel}</span> : null}
          <ActivationControl />
          <span className="app-topbar__divider" aria-hidden="true" />
          <div ref={rootRef} className="app-account">
            <button
              type="button"
              className="app-account__btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={name ? `Accountmenu voor ${name}` : "Accountmenu"}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="app-account__avatar" aria-hidden="true">
                {initials}
              </span>
              <span className="app-topbar-user">
                <span className="app-account__name">{name || email}</span>
                {roleLabel ? (
                  <span className="app-account__role" title={roleLabelFull}>
                    {roleLabel}
                  </span>
                ) : null}
              </span>
            </button>
            {menuOpen && (
              <div role="menu" aria-label="Accountmenu" className="app-account__menu">
                <div className="app-account__info">
                  <div className="app-account__info-name">{name || "Aangemeld"}</div>
                  {email && (
                    <div className="app-account__info-email" title={email}>
                      {email}
                    </div>
                  )}
                  {roleLabelFull && <div className="app-account__info-role">Rol: {roleLabelFull}</div>}
                </div>
                <button
                  role="menuitem"
                  type="button"
                  className="app-account__logout"
                  onClick={() => {
                    clearAuthSessionState();
                    instance.logoutRedirect();
                  }}
                >
                  <Icon name="logout" size={15} />
                  Afmelden
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}
